import PropTypes from 'prop-types';
import { css } from '@emotion/react';
import MuiButton from '@material-ui/core/Button';

import { TRACK_CATEGORIES } from 'lib/tracking';

import { colors } from 'styles/theme';
import { setOpacity, tabletUp } from 'styles/utils';

import CircularProgress from '../CircularProgress/CircularProgress';
import EventTrack from '../EventTrack/EventTrack';

const SKINS = {
  PRIMARY: 'primary',
  LIGHT: 'light',
};

const btnSkins = {
  [SKINS.PRIMARY]: css`
    /* Set style when using variant="contained" */
    &.MuiButton-contained {
      color: ${colors.white};
      background-color: ${colors.darkPurple};

      ${tabletUp} {
        &:hover {
          background-color: ${colors.purple};
        }
      }

      &:disabled {
        color: ${setOpacity(colors.white, 0.4)};
      }
    }

    /* Set style when using variant="text" */
    &.MuiButton-text {
      color: ${colors.darkPurple};
      background-color: transparent;

      &:disabled {
        opacity: 0.8;
        color: ${colors.brownGray};
      }
    }

    /* Set style when using variant="outlined" */
    &.MuiButton-outlined {
      color: ${colors.darkPurple};
      background-color: transparent;
      border-color: ${colors.darkPurple};

      ${tabletUp} {
        &:hover {
          color: ${colors.white};
          background-color: ${colors.darkPurple};
        }
      }

      &:disabled {
        opacity: 0.4;
      }
    }

    &.MuiButton-outlinedSizeSmall {
      border-width: 1px;
    }
  `,

  [SKINS.LIGHT]: css`
    /* Set style when using variant="contained" */
    &.MuiButton-contained {
      color: ${colors.darkPurple};
      background-color: ${colors.white};

      ${tabletUp} {
        &:hover {
          background-color: ${colors.white};
        }
      }

      &:disabled {
        color: ${setOpacity(colors.darkPurple, 0.4)};
      }
    }
  `,
};

const btnProgressSkins = {
  [SKINS.PRIMARY]: {
    text: 'darkPurple',
    contained: 'white',
    outlined: 'darkPurple',
  },
  [SKINS.LIGHT]: {
    text: 'darkPurple',
    contained: 'darkPurple',
    outlined: 'darkPurple',
  },
};

const btnCss = skin => css`
  &.MuiButtonBase-root {
    font-size: 1.8rem;
    padding: 16px 45px;
    border-radius: 2em;
    text-transform: unset;
    line-height: unset;
    border-width: 2px;
    border-color: transparent;
    border-style: solid;

    /* Set button's styling by a skin */
    ${btnSkins[skin]}
  }

  /* Applied when size="small" */
  &.MuiButton-textSizeSmall,
  &.MuiButton-outlinedSizeSmall,
  &.MuiButton-containedSizeSmall {
    font-size: 1.6rem;
    padding: 7px 15px;
    border-width: 1px;
  }
`;

const btnContentCss = showProgress => css`
  /* Hide button's content when showing progress */
  visibility: ${showProgress && 'hidden'};
`;

/**
 * The component is making use of Material UI's Button.
 *
 * @see
 * [Material UI Button](https://material-ui.com/components/buttons/)
 *
 * @param {object} props
 */
function Button({
  children,
  skin = 'primary',
  variant = 'contained',
  type = 'submit',
  disableElevation = true,
  showProgress = false,
  track,
  ...restProps
}) {
  return (
    <EventTrack
      shouldTrack={Boolean(track)}
      track={{ category: TRACK_CATEGORIES.CTA_CLICK, ...track }}
    >
      <MuiButton
        disableElevation={disableElevation}
        css={btnCss(skin)}
        variant={variant}
        type={type}
        {...restProps}
      >
        <span css={btnContentCss(showProgress)}>{children}</span>
        {showProgress && (
          <CircularProgress
            color={btnProgressSkins[skin]?.[variant]}
            size={1.5}
            center
          />
        )}
      </MuiButton>
    </EventTrack>
  );
}

Button.propTypes = {
  children: PropTypes.node.isRequired,
  /** Set button's styling by a skin */
  skin: PropTypes.oneOf(Object.values(SKINS)),
  /** If true, there's no background that creates an elevation effect */
  disableElevation: PropTypes.bool,
  /** Show progress indication */
  showProgress: PropTypes.bool,
  /** Specifies the type of a button */
  type: PropTypes.oneOf(['button', 'reset', 'submit']),
  /** Track object for analytics purposes */
  track: PropTypes.shape({
    event: PropTypes.string,
    category: PropTypes.string,
    label: PropTypes.string,
  }),
  variant: PropTypes.string,
};

export default Button;
